/** The "Needs you" page's rules, kept free of React so they are unit-tested: labels, the edit diff, batch approval, keys. */
export type Group = 'approval' | 'reply' | 'task';
export const groupTitles: Record<Group, string> = { approval: 'Ready to approve', reply: 'Replies to review', task: 'Tasks' };

/**
 * Monk-style triage chips over the existing queue. Uses reply.kind and blockers already on the item — no new API.
 * `held` = anything with a non-`needs_review` blocker (why this customer is not moving).
 */
export type TriageFilter = 'all' | 'held' | 'payment_claim' | 'wrong_contact' | 'dispute' | 'vendor_compliance';
export const triageLabels: Record<TriageFilter, string> = {
  all: 'All',
  held: 'Held / blocked',
  payment_claim: 'Payment claims',
  wrong_contact: 'Wrong contact',
  dispute: 'Disputes',
  vendor_compliance: 'Vendor compliance',
};
const triageOrder: TriageFilter[] = ['all', 'held', 'payment_claim', 'wrong_contact', 'dispute', 'vendor_compliance'];

export interface TriageItem {
  reply?: { kind: string } | null;
  blockers?: readonly { code: string }[];
}

/** Which triage buckets an item belongs to (an item can be both held and a payment claim). */
export function itemTriage(item: TriageItem): TriageFilter[] {
  const tags: TriageFilter[] = [];
  if ((item.blockers ?? []).some(b => b.code !== 'needs_review')) tags.push('held');
  const kind = item.reply?.kind;
  if (kind === 'payment_claim' || kind === 'paid_claim') tags.push('payment_claim');
  if (kind === 'wrong_contact') tags.push('wrong_contact');
  if (kind === 'dispute') tags.push('dispute');
  if (kind === 'vendor_compliance') tags.push('vendor_compliance');
  return tags;
}

export function matchesTriage(item: TriageItem, filter: TriageFilter) {
  return filter === 'all' || itemTriage(item).includes(filter);
}

/** Counts for chips that have at least one item; `all` is always included. */
export function triageChipCounts(items: readonly TriageItem[]): { id: TriageFilter; label: string; count: number }[] {
  const counts: Record<TriageFilter, number> = {
    all: items.length, held: 0, payment_claim: 0, wrong_contact: 0, dispute: 0, vendor_compliance: 0,
  };
  for (const item of items) for (const tag of itemTriage(item)) counts[tag] += 1;
  return triageOrder
    .filter(id => id === 'all' || counts[id] > 0)
    .map(id => ({ id, label: triageLabels[id], count: counts[id] }));
}

const actionLabels: Record<string, string> = {
  approve: 'Approve', edit: 'Edit first', reject: 'Reject', snooze: 'Hold off', wait: 'Snoozed', done: 'Mark done',
  reassign: 'Hand off', approve_pause: 'Approve pause', confirm_promise: 'Confirm promise', check_payment: 'Check payment',
  open_dispute: 'Open dispute', draft_response: 'Draft a reply', delay: 'Delay reminders', update_contact: 'Update contact',
  assign: 'Assign follow-up', review: 'Read it', not_promise: 'Not a promise',
};
export const actionLabel = (action: string) => actionLabels[action] ?? 'Review';
/** Chip colour: go-ahead actions read green, refusals red, holds amber, everything else neutral. */
export function actionTone(action: string): 'go' | 'stop' | 'hold' | 'look' {
  if (['approve', 'done', 'confirm_promise', 'approve_pause'].includes(action)) return 'go';
  if (action === 'reject') return 'stop';
  if (['snooze', 'wait', 'delay'].includes(action)) return 'hold';
  return 'look';
}
/** "today", "1 day", "4 days"; tasks count from their due date. */
export function ageText(kind: Group, days: number) {
  if (kind === 'task') return days ? `${days} ${days === 1 ? 'day' : 'days'} late` : 'Due today';
  return days ? `${days}d` : 'Today';
}

/** One run of a word diff: unchanged, added by the person or removed from Amy's draft. */
export interface DiffPart { kind: 'same' | 'add' | 'del'; text: string }
/** A word-level diff of `before` (Amy's draft) against `after` (what a person saved), whitespace kept with its word. */
export function wordDiff(before: string, after: string): DiffPart[] {
  const a = before.split(/(?<=\s)(?=\S)/); const b = after.split(/(?<=\s)(?=\S)/);
  // Longest common subsequence over tokens; drafts are short enough for the full table.
  const table = Array.from({ length: a.length + 1 }, () => new Uint32Array(b.length + 1));
  for (let i = a.length - 1; i >= 0; i--) for (let j = b.length - 1; j >= 0; j--) {
    table[i]![j] = a[i] === b[j] ? table[i + 1]![j + 1]! + 1 : Math.max(table[i + 1]![j]!, table[i]![j + 1]!);
  }
  const parts: DiffPart[] = [];
  const push = (kind: DiffPart['kind'], text: string) => {
    const last = parts.at(-1);
    if (last?.kind === kind) last.text += text; else parts.push({ kind, text });
  };
  let i = 0; let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) { push('same', a[i]!); i++; j++; }
    else if (table[i + 1]![j]! >= table[i]![j + 1]!) push('del', a[i++]!);
    else push('add', b[j++]!);
  }
  while (i < a.length) push('del', a[i++]!);
  while (j < b.length) push('add', b[j++]!);
  return parts;
}

interface Batchable { key: string; batchKey: string | null }
/** Items can be approved together only when every one is batchable and all share one policy step and action type. */
export function compatible(items: readonly Batchable[]) {
  const first = items[0]?.batchKey;
  return Boolean(first) && items.every(i => i.batchKey === first);
}
/** Whether `item` may join a selection: the same batch key as what is already selected. */
export function canJoin(selection: readonly Batchable[], item: Batchable) {
  return Boolean(item.batchKey) && (!selection.length || selection[0]!.batchKey === item.batchKey);
}
export interface BatchResult { key: string; ok: boolean; message: string }
/**
 * Approves one item at a time through `decide`, which makes that item's own checked decision (its revision, a fresh
 * idempotency key, the CSRF header). A refusal is recorded and the rest still run; nothing is retried or skipped.
 */
export async function runBatch<T extends Batchable>(items: readonly T[], decide: (item: T) => Promise<string>) {
  if (!compatible(items)) throw new Error('Choose drafts at the same reminder step to approve together.');
  const results: BatchResult[] = [];
  for (const item of items) {
    try { results.push({ key: item.key, ok: true, message: await decide(item) }); }
    catch (error) { results.push({ key: item.key, ok: false, message: error instanceof Error ? error.message : 'Not approved.' }); }
  }
  return results;
}

export type KeyCommand = 'next' | 'previous' | 'approve' | 'edit' | 'reject' | 'snooze' | 'select';
const keys: Record<string, KeyCommand> = { j: 'next', k: 'previous', a: 'approve', e: 'edit', r: 'reject', s: 'snooze', x: 'select',
  ArrowDown: 'next', ArrowUp: 'previous' };
/** The page's single-key commands; none fire while typing or with a modifier held. */
interface KeyInput { key: string; metaKey: boolean; ctrlKey: boolean; altKey: boolean; target: unknown }
export function keyCommand(event: KeyInput): KeyCommand | null {
  if (event.metaKey || event.ctrlKey || event.altKey) return null;
  const target = event.target as { tagName?: string; isContentEditable?: boolean } | null;
  if (target && (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName ?? '') || target.isContentEditable)) return null;
  return keys[event.key] ?? null;
}
